const CACHE_NAME = `hallo-file-cache-${window.APP_ID}`;
const CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000;

class ImageCacheService {
  constructor() {
    this.cacheAvailable = 'caches' in window;
    this.initialized = false;
    this.urlCache = new Map(); // Локальный кэш URL объектов
    this.loadingPromises = new Map(); // Хранение промисов загрузки
    this.baseUrlMap = new Map(); // Карта для отслеживания версий изображений
  }

  needsLoading(url) {
    return !this.urlCache.has(url);
  }

  getLocalCachedUrl(url) {
    return this.urlCache.get(url);
  }

  setLocalCachedUrl(url, blobUrl) {
    this.urlCache.set(url, blobUrl);
  }

  async initializeCache() {
    if (!this.cacheAvailable || this.initialized) return;

    try {
      await this.clearExpiredCache();
      await this.loadCacheIntoMap();
      this.initialized = true;
    } catch (error) {
      console.warn('Failed to initialize cache:', error);
    }
  }

  async loadCacheIntoMap() {
    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();
      const urlGroups = new Map(); // Временная карта для группировки URL по базовому адресу

      // Сначала группируем URL по базовому адресу
      for (const request of keys) {
        const url = request.url;
        const baseUrl = this.getBaseUrl(url);
        const timeParam = this.getTimeParam(url);

        if (!urlGroups.has(baseUrl)) {
          urlGroups.set(baseUrl, []);
        }
        urlGroups.get(baseUrl).push({ url, timeParam });
      }

      // Обрабатываем каждую группу URL
      for (const [baseUrl, versions] of urlGroups) {
        // Сортируем версии по времени (параметр t) по убыванию
        versions.sort((a, b) => {
          return (b.timeParam || 0) - (a.timeParam || 0);
        });

        // Оставляем только самую новую версию
        const latestVersion = versions[0];
        this.baseUrlMap.set(baseUrl, latestVersion.url);

        // Удаляем старые версии
        for (let i = 1; i < versions.length; i++) {
          const oldUrl = versions[i].url;
          console.log('Removing old cached version:', oldUrl);
          await cache.delete(oldUrl);
        }

        // Загружаем актуальную версию в кэш
        const response = await cache.match(latestVersion.url);
        if (response) {
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          this.setLocalCachedUrl(latestVersion.url, objectUrl);
        }
      }
    } catch (error) {
      console.warn('Failed to load cache into map:', error);
    }
  }

  async clearExpiredCache() {
    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();
      const now = Date.now();

      for (const request of keys) {
        const response = await cache.match(request);
        const cacheDate = new Date(response.headers.get('cache-date'));

        if (now - cacheDate.getTime() > CACHE_EXPIRATION) {
          await cache.delete(request);
        }
      }
    } catch (error) {
      console.warn('Failed to clear expired cache:', error);
    }
  }

  async clearCache() {
    console.log('Clearing cache...');
    try {
      await caches.delete(CACHE_NAME);
      this.urlCache.clear();
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  async fullReset() {
    try {
      this.clearUnusedUrls();
      await this.clearCache();
      this.initialized = false;
      this.urlCache.clear();
      this.loadingPromises.clear();
      this.baseUrlMap.clear();
      console.log('Cache service fully reset');
    } catch (error) {
      console.warn('Failed to fully reset cache service:', error);
    }
  }

  async preloadCriticalImages(criticalImages, onProgress) {
    if (!this.cacheAvailable) return;

    if (!this.initialized) {
      await this.initializeCache();
    }

    let loaded = 0;
    const total = criticalImages.length;

    try {
      for (const url of criticalImages) {
        try {
          await this.getCachedImage(url);
          loaded++;
          onProgress?.((loaded / total) * 100);
        } catch (error) {
          console.warn(`Failed to preload critical image ${url}:`, error);
          loaded++;
          onProgress?.((loaded / total) * 100);
        }
      }
    } catch (error) {
      console.warn('Failed to preload critical images:', error);
    }
  }

  async preloadNonCriticalImages(nonCriticalImages) {
    if (!this.cacheAvailable) return;

    try {
      const batchSize = 5;
      for (let i = 0; i < nonCriticalImages.length; i += batchSize) {
        const batch = nonCriticalImages.slice(i, i + batchSize);
        await Promise.all(
          batch.map((url) =>
            this.getCachedImage(url).catch((error) => {
              console.warn(
                `Failed to preload non-critical image ${url}:`,
                error
              );
            })
          )
        );
      }
    } catch (error) {
      console.warn('Failed to preload non-critical images:', error);
    }
  }

  async getCachedImage(url) {
    if (!this.cacheAvailable) return url;

    const cachedUrl = this.getLocalCachedUrl(url);
    if (cachedUrl) {
      return cachedUrl;
    }

    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    const loadPromise = this._loadAndCacheImage(url);
    this.loadingPromises.set(url, loadPromise);

    try {
      const result = await loadPromise;
      this.loadingPromises.delete(url);
      return result;
    } catch (error) {
      this.loadingPromises.delete(url);
      throw error;
    }
  }

  async _loadAndCacheImage(url) {
    try {
      const cache = await caches.open(CACHE_NAME);
      const baseUrl = this.getBaseUrl(url);

      // Проверяем и удаляем старую версию
      if (this.baseUrlMap.has(baseUrl)) {
        const oldUrl = this.baseUrlMap.get(baseUrl);
        // Убедимся, что это действительно другая версия
        if (oldUrl !== url) {
          console.log('Removing old version:', oldUrl);
          // Удаляем из Cache API
          await cache.delete(oldUrl);
          // Удаляем из urlCache
          const oldObjectUrl = this.urlCache.get(oldUrl);
          if (oldObjectUrl) {
            URL.revokeObjectURL(oldObjectUrl);
            this.urlCache.delete(oldUrl);
          }
          // Удаляем из loadingPromises если есть
          if (this.loadingPromises.has(oldUrl)) {
            this.loadingPromises.delete(oldUrl);
          }
        }
      }

      // Сохраняем новую версию URL в карту
      this.baseUrlMap.set(baseUrl, url);

      const response = await fetch(url);
      const blob = await response.clone().blob();

      const headers = new Headers(response.headers);
      headers.append('cache-date', new Date().toUTCString());

      const cachedResponseWithDate = new Response(blob, {
        status: response.status,
        statusText: response.statusText,
        headers: headers,
      });

      await cache.put(url, cachedResponseWithDate);

      const objectUrl = URL.createObjectURL(blob);
      this.setLocalCachedUrl(url, objectUrl);
      return objectUrl;
    } catch (error) {
      console.warn(`Failed to load and cache image ${url}:`, error);
      return url;
    }
  }

  // Получаем базовый URL без параметра времени
  getBaseUrl(url) {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.delete('t');
      return urlObj.toString();
    } catch (error) {
      console.warn('Failed to get base URL:', error);
      return url;
    }
  }

  // Получаем параметр времени из URL
  getTimeParam(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('t');
    } catch (error) {
      return null;
    }
  }

  clearUnusedUrls() {
    for (const [url, blobUrl] of this.urlCache.entries()) {
      URL.revokeObjectURL(blobUrl);
      this.urlCache.delete(url);
    }
  }
}

export const imageCacheService = new ImageCacheService();
